import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete
} from '@react-google-maps/api';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { MAP_CONFIG } from '../../config/mapConfig';

// Librerías de Google Maps a cargar
const libraries = ['places'];

// Estilos del contenedor del mapa
const containerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
};

/**
 * Componente MapaPicker - Selector de ubicación con Google Maps
 *
 * @param {Object} props
 * @param {number} props.latitud - Latitud inicial
 * @param {number} props.longitud - Longitud inicial
 * @param {string} props.direccion - Dirección inicial
 * @param {function} props.onChange - Callback cuando cambia la ubicación: ({latitud, longitud, direccion}) => void
 * @param {boolean} props.disabled - Si está deshabilitado
 * @param {string} props.label - Etiqueta del campo
 * @param {boolean} props.showSearch - Mostrar barra de búsqueda
 * @param {number} props.height - Altura del mapa en px
 */
const MapaPicker = ({
  latitud,
  longitud,
  direccion = '',
  onChange,
  disabled = false,
  label = 'Seleccionar ubicación',
  showSearch = true,
  height = 300
}) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(direccion);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  // Cargar Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAP_CONFIG.googleMapsApiKey,
    libraries
  });

  // Centro inicial del mapa
  const center = {
    lat: latitud || MAP_CONFIG.defaultCenter.lat,
    lng: longitud || MAP_CONFIG.defaultCenter.lng
  };

  // Cuando cambia la posición del marcador
  useEffect(() => {
    if (latitud && longitud) {
      setMarker({ lat: parseFloat(latitud), lng: parseFloat(longitud) });
    }
  }, [latitud, longitud]);

  // Cuando cambia la dirección externa
  useEffect(() => {
    setCurrentAddress(direccion);
  }, [direccion]);

  // Cuando se carga el mapa
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Cuando se desmonta el mapa
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Geocodificación inversa: obtener dirección desde coordenadas
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results && response.results[0]) {
        return response.results[0].formatted_address;
      }
      return '';
    } catch (err) {
      console.error('Error en geocodificación inversa:', err);
      return '';
    }
  };

  // Click en el mapa - colocar marcador
  const handleMapClick = async (event) => {
    if (disabled) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarker({ lat, lng });
    setLoading(true);
    setError('');

    try {
      const address = await getAddressFromCoords(lat, lng);
      setCurrentAddress(address);

      if (onChange) {
        onChange({
          latitud: lat,
          longitud: lng,
          direccion: address
        });
      }
    } catch (err) {
      setError('Error al obtener la dirección');
    } finally {
      setLoading(false);
    }
  };

  // Obtener ubicación actual del usuario
  const handleGetCurrentLocation = () => {
    if (disabled) return;

    if (!navigator.geolocation) {
      setError('Geolocalización no soportada por este navegador');
      return;
    }

    setLoading(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setMarker({ lat, lng });

        // Centrar mapa en la ubicación
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }

        try {
          const address = await getAddressFromCoords(lat, lng);
          setCurrentAddress(address);

          if (onChange) {
            onChange({
              latitud: lat,
              longitud: lng,
              direccion: address
            });
          }
        } catch (err) {
          setError('Error al obtener la dirección');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Permiso de ubicación denegado');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Ubicación no disponible');
            break;
          case err.TIMEOUT:
            setError('Tiempo de espera agotado');
            break;
          default:
            setError('Error al obtener ubicación');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Cuando se carga el Autocomplete
  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  // Cuando se selecciona un lugar del autocompletado
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || place.name;

        setMarker({ lat, lng });
        setCurrentAddress(address);

        // Centrar mapa en la ubicación seleccionada
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }

        if (onChange) {
          onChange({
            latitud: lat,
            longitud: lng,
            direccion: address
          });
        }
      }
    }
  };

  // Limpiar selección
  const handleClear = () => {
    setMarker(null);
    setCurrentAddress('');
    setError('');

    if (onChange) {
      onChange({
        latitud: null,
        longitud: null,
        direccion: ''
      });
    }

    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  // Error al cargar Google Maps
  if (loadError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error al cargar Google Maps. Verifica tu conexión a internet.
      </Alert>
    );
  }

  // Cargando Google Maps
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando mapa...</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
      {/* Label */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1A237E', fontWeight: 600 }}>
        <LocationIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
        {label}
      </Typography>

      {/* Barra de búsqueda y botón de ubicación */}
      {showSearch && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Autocomplete
            onLoad={onAutocompleteLoad}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: 'pe' }, // Restringir a Perú
              types: ['address']
            }}
          >
            <TextField
              inputRef={searchInputRef}
              fullWidth
              size="small"
              placeholder="Buscar dirección..."
              disabled={disabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: currentAddress && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear} disabled={disabled}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Autocomplete>

          <Button
            variant="outlined"
            onClick={handleGetCurrentLocation}
            disabled={disabled || loading}
            sx={{ minWidth: 'auto', px: 2 }}
            title="Obtener mi ubicación"
          >
            {loading ? <CircularProgress size={20} /> : <MyLocationIcon />}
          </Button>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Mapa */}
      <GoogleMap
        mapContainerStyle={{ ...containerStyle, height: height }}
        center={marker || center}
        zoom={marker ? 17 : MAP_CONFIG.defaultZoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: disabled ? 'none' : 'greedy'
        }}
      >
        {marker && (
          <Marker
            position={marker}
            draggable={!disabled}
            onDragEnd={async (e) => {
              const lat = e.latLng.lat();
              const lng = e.latLng.lng();
              setMarker({ lat, lng });

              setLoading(true);
              try {
                const address = await getAddressFromCoords(lat, lng);
                setCurrentAddress(address);

                if (onChange) {
                  onChange({
                    latitud: lat,
                    longitud: lng,
                    direccion: address
                  });
                }
              } finally {
                setLoading(false);
              }
            }}
          />
        )}
      </GoogleMap>

      {/* Dirección seleccionada */}
      {currentAddress && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(26, 35, 126, 0.05)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#1A237E' }}>
            <strong>Dirección:</strong> {currentAddress}
          </Typography>
          {marker && (
            <Typography variant="caption" color="text.secondary">
              Coordenadas: {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
            </Typography>
          )}
        </Box>
      )}

      {/* Instrucciones */}
      {!currentAddress && !disabled && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Haz clic en el mapa para seleccionar una ubicación o usa la barra de búsqueda
        </Typography>
      )}
    </Paper>
  );
};

export default MapaPicker;
